/**
 * Common roller/cover control capabilities
 * Used by devices with roller shutters, blinds, curtains
 */

/**
 * Cover open action
 */
export function createCoverOpenAction() {
	return {
		name: 'Open Cover',
		description: 'Open the cover fully',
		parameters: {},
		executeAction: async (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;

			return {
				method: 'Cover.Open',
				params: {
					id: channelId,
				},
			};
		},
	};
}

/**
 * Cover close action
 */
export function createCoverCloseAction() {
	return {
		name: 'Close Cover',
		description: 'Close the cover fully',
		parameters: {},
		executeAction: async (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;

			return {
				method: 'Cover.Close',
				params: {
					id: channelId,
				},
			};
		},
	};
}

/**
 * Cover stop action
 */
export function createCoverStopAction() {
	return {
		name: 'Stop Cover',
		description: 'Stop cover movement',
		parameters: {},
		executeAction: async (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;

			return {
				method: 'Cover.Stop',
				params: {
					id: channelId,
				},
			};
		},
	};
}

/**
 * Cover go to position action (requires calibration)
 */
export function createCoverGoToPositionAction() {
	return {
		name: 'Go To Position',
		description: 'Move cover to specific position (requires calibration)',
		parameters: {
			position: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Target position (0% = open, 100% = closed)',
			},
		},
		executeAction: async (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const { position } = params;

			return {
				method: 'Cover.GoToPosition',
				params: {
					id: channelId,
					pos: position,
				},
			};
		},
	};
}

/**
 * Cover state condition
 */
export function createCoverStateCondition() {
	return {
		name: 'Cover State',
		description: 'Check current cover state',
		parameters: {
			state: {
				type: 'enum',
				required: true,
				values: ['open', 'closed', 'stopped', 'opening', 'closing'],
				description: 'Expected cover state',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentState = device.status?.[channel]?.state;
			return currentState === params.state;
		},
	};
}

/**
 * Cover position condition (requires calibration)
 */
export function createCoverPositionCondition() {
	return {
		name: 'Cover Position',
		description: 'Check cover position (requires calibration)',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['equal_to', 'greater_than', 'less_than', 'between'],
				default: 'equal_to',
				description: 'Comparison operator',
			},
			position: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Position threshold',
			},
			position_max: {
				type: 'number',
				required: false,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Upper position threshold (for "between")',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentPosition = device.status?.[channel]?.current_pos;
			const { operator, position, position_max } = params;

			if (currentPosition === undefined) return false;

			switch (operator) {
				case 'equal_to':
					return Math.abs(currentPosition - position) < 2;
				case 'greater_than':
					return currentPosition > position;
				case 'less_than':
					return currentPosition < position;
				case 'between':
					return currentPosition >= position && currentPosition <= (position_max || 100);
				default:
					return false;
			}
		},
	};
}

/**
 * Cover state changed trigger
 */
export function createCoverStateChangedTrigger() {
	return {
		name: 'Cover State Changed',
		description: 'Fires when cover state changes',
		parameters: {
			from_state: {
				type: 'enum',
				required: false,
				values: ['any', 'open', 'closed', 'stopped', 'opening', 'closing'],
				default: 'any',
				description: 'Previous state filter',
			},
			to_state: {
				type: 'enum',
				required: false,
				values: ['any', 'open', 'closed', 'stopped', 'opening', 'closing'],
				default: 'any',
				description: 'New state filter',
			},
		},
		evaluateTrigger: (device, channel, previousStatus, currentStatus, params) => {
			const prevState = previousStatus?.[channel]?.state;
			const currState = currentStatus?.[channel]?.state;

			if (prevState === currState) return false;

			const { from_state, to_state } = params;
			const prevMatch = from_state === 'any' || prevState === from_state;
			const currMatch = to_state === 'any' || currState === to_state;

			return prevMatch && currMatch;
		},
	};
}
